import { Controller } from "@hotwired/stimulus"

// Document Upload Controller
// Handles drag-and-drop and click-to-upload for the Application Portal
export default class extends Controller {
  static targets = ["input", "dropzone", "idle", "uploading", "analysing", "error", "errorMessage", "preview", "rejectionNotice"]
  static values = {
    url: String,
    checkType: String
  }

  connect() {
    this.dragCounter = 0
    this.analysingTimeout = null
  }

  disconnect() {
    if (this.analysingTimeout) {
      clearTimeout(this.analysingTimeout)
    }
  }

  openFileDialog() {
    this.inputTarget.click()
  }

  dragenter(event) {
    event.preventDefault()
    this.dragCounter++
    this.dropzoneTarget.classList.add("border-gray-900", "bg-gray-50")
    this.dropzoneTarget.classList.remove("border-gray-300")
  }

  dragleave(event) {
    event.preventDefault()
    this.dragCounter--
    if (this.dragCounter === 0) {
      this.dropzoneTarget.classList.remove("border-gray-900", "bg-gray-50")
      this.dropzoneTarget.classList.add("border-gray-300")
    }
  }

  dragover(event) {
    event.preventDefault()
  }

  drop(event) {
    event.preventDefault()
    this.dragCounter = 0
    this.dropzoneTarget.classList.remove("border-gray-900", "bg-gray-50")
    this.dropzoneTarget.classList.add("border-gray-300")

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.uploadFile(files[0])
    }
  }

  upload(event) {
    const file = event.target.files[0]
    if (file) {
      this.uploadFile(file)
    }
  }

  async uploadFile(file) {
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      this.showError("File size must be less than 10MB")
      return
    }

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"]
    if (!validTypes.includes(file.type)) {
      this.showError("Please upload a valid image or PDF file")
      return
    }

    // Show uploading state
    this.showUploading()

    try {
      const formData = new FormData()
      formData.append("document", file)
      formData.append("check_type", this.checkTypeValue)

      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "text/vnd.turbo-stream.html, text/html, application/xhtml+xml"
        }
      })

      if (response.ok) {
        // Turbo will handle the stream response
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        const errorData = await response.json().catch(() => ({}))
        this.showError(errorData.error || "Upload failed. Please try again.")
      }
    } catch (error) {
      console.error("Upload error:", error)
      this.showError("Upload failed. Please check your connection and try again.")
    }
  }

  showUploading() {
    // Hide the rejection notice if re-uploading after rejection
    if (this.hasRejectionNoticeTarget) this.rejectionNoticeTarget.classList.add("hidden")
    // Hide the document preview if replacing
    if (this.hasPreviewTarget) this.previewTarget.classList.add("hidden")
    if (this.hasIdleTarget) this.idleTarget.classList.add("hidden")
    if (this.hasUploadingTarget) this.uploadingTarget.classList.remove("hidden")
    if (this.hasAnalysingTarget) this.analysingTarget.classList.add("hidden")
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")

    // After 1.5 seconds, switch to "Analysing" state
    // (file upload is usually quick, most time is spent on AI analysis)
    this.analysingTimeout = setTimeout(() => {
      this.showAnalysing()
    }, 1500)
  }

  showAnalysing() {
    if (this.hasUploadingTarget) this.uploadingTarget.classList.add("hidden")
    if (this.hasAnalysingTarget) this.analysingTarget.classList.remove("hidden")
  }

  showError(message) {
    // Clear the analysing timeout
    if (this.analysingTimeout) {
      clearTimeout(this.analysingTimeout)
      this.analysingTimeout = null
    }

    // Show the preview again if it exists (replacement failed)
    if (this.hasPreviewTarget) this.previewTarget.classList.remove("hidden")
    if (this.hasIdleTarget) this.idleTarget.classList.add("hidden")
    if (this.hasUploadingTarget) this.uploadingTarget.classList.add("hidden")
    if (this.hasAnalysingTarget) this.analysingTarget.classList.add("hidden")
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("hidden")
      if (this.hasErrorMessageTarget) {
        this.errorMessageTarget.textContent = message
      }
    }

    // Reset to idle state after 3 seconds
    setTimeout(() => {
      this.resetToIdle()
    }, 3000)
  }

  resetToIdle() {
    // Clear the analysing timeout
    if (this.analysingTimeout) {
      clearTimeout(this.analysingTimeout)
      this.analysingTimeout = null
    }

    // Show the preview again if it exists
    if (this.hasPreviewTarget) this.previewTarget.classList.remove("hidden")
    if (this.hasIdleTarget) this.idleTarget.classList.remove("hidden")
    if (this.hasUploadingTarget) this.uploadingTarget.classList.add("hidden")
    if (this.hasAnalysingTarget) this.analysingTarget.classList.add("hidden")
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }
}
